import { useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import { List, Divider, Link, Collapse } from "@mui/material";
import Icon from "@mui/material/Icon";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";  // Importing SidenavCollapse component

// Material Dashboard 2 React context
import { useMaterialUIController, setMiniSidenav, setTransparentSidenav, setWhiteSidenav } from "context";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

import Avatar from "@mui/material/Avatar";
import ListItem from "@mui/material/ListItem";
import { useMediaQuery } from '@mui/material';



function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const [openCollapse, setOpenCollapse] = useState({}); // Track open collapses
  const [sideNavWidth, setSideNavWidth] = useState(250); // Track open collapses

  let textColor = "white";
  const isSmallScreen = useMediaQuery('(max-width:1024px)');

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
      setSideNavWidth(250);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Function to toggle collapse state for submenus
  const handleCollapseToggle = (key) => {
    setOpenCollapse((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));

    setSideNavWidth(250);

  };

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route, collapse }) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
        <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
            onClick={() => handleCollapseToggle(key)} // Toggle collapse
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse
            name={sideNavWidth === 250 && name}
            icon={icon}
            active={key === collapseName}
            onClick={() => handleCollapseToggle(key)} // Toggle collapse
          />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    // Render collapse if it exists
    if (collapse) {
      returnValue = (
        <div key={key}>
          <NavLink to={route}>
            <SidenavCollapse
              name={sideNavWidth === 250 && name}
              icon={icon}
              active={key === collapseName}
              onClick={() => handleCollapseToggle(key)} // Toggle collapse
            />
          </NavLink>
          <Collapse in={openCollapse[key]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {collapse.map(({ name, route, key }) => (
                <NavLink key={key} to={route}>
                  <SidenavCollapse
                    icon={<Icon fontSize="small">subdirectory_arrow_right</Icon>}
                    name={sideNavWidth === 250 && name}
                    active={location.pathname === route}
                  />
                </NavLink>
              ))}
            </List>
          </Collapse>
        </div>
      );
    }

    return returnValue;
  });


  // Function to get the user object from localStorage
  const getUserFromLocalStorage = () => {
    const storedUser = localStorage.getItem("user");

    // Check if user exists in localStorage
    if (storedUser) {
      try {
        // Parse the stored user object
        const userObject = JSON.parse(storedUser).user;

        // Check if user object exists and has first_name and last_name
        if (userObject && userObject.first_name && userObject.last_name) {
          return userObject;
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
    return null;
  };

  // Get the user object from localStorage
  const userObject = getUserFromLocalStorage();

  // Step 1: Define color for each letter
  const letterColors = {
    A: "#e74c3c", // Red
    B: "#2980b9", // Blue
    C: "#27ae60", // Green
    D: "#8e44ad", // Purple
    E: "#f39c12", // Orange
    F: "#1abc9c", // Teal
    G: "#c0392b",
    H: "#2c3e50",
    I: "#d35400",
    J: "#7f8c8d",
    K: "#16a085",
    L: "#34495e",
    M: "#9b59b6",
    N: "#f1c40f",
    O: "#e67e22",
    P: "#bdc3c7",
    Q: "#95a5a6",
    R: "#ff6b6b",
    S: "#48dbfb",
    T: "#1e3799",
    U: "#ff9ff3",
    V: "#10ac84",
    W: "#00d2d3",
    X: "#5f27cd",
    Y: "#54a0ff",
    Z: "#576574"
  };

  // Step 2: Get user's full name
  const fullName =
    userObject?.first_name && userObject?.last_name
      ? `${userObject.first_name} ${userObject.last_name}`
      : "John Doe";

  // Step 3: Get first character and its color
  const initial = fullName.charAt(0).toUpperCase();
  const bgColor = letterColors[initial] || "#333"; // fallback color

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode ,sideNavWidth}}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between', // 👈 magic line
      }}
    >

      <MDBox flexGrow={1}>

        <MDBox pt={3} pb={1} px={4} textAlign="center" mb={1} sx={{ borderBottom: '1px solid lightgrey' }}>
          <MDBox
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1.625}
            onClick={closeSidenav}
            sx={{ cursor: "pointer" }}
          >
            <MDTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold" }}>close</Icon>
            </MDTypography>
          </MDBox>
          <MDBox 
          // component={NavLink} 
          // to="/" 
          display="flex" 
          alignItems="center"
          >
            {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
            <MDBox 
            // width={!brandName && "100%"}
            width={brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
            >
              {/* <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName} 
              <ChevronLeftIcon
                sx={{
                  ml: 1,            // Left margin (theme spacing unit = 8px, so 1 = 8px)
                  fontSize: '20px !important', // force increase size
                  // color: '#333'     // Optional: dark color
                }}
              />
            </MDTypography> */}

              <MDBox sx={{
                display: 'flex',        // 👈 Aligns children (text + icon) horizontally
                alignItems: 'center',   // 👈 Vertically center-aligns icon with text
                justifyContent:'space-between',
                width:'100%'
              }}>

                <MDTypography
                  component="h6"
                  // component={NavLink}
                  //  to="/"
                  variant="button"
                  fontWeight="medium"
                  color={textColor}
                  sx={{
                    display: 'flex',        // 👈 Aligns children (text + icon) horizontally
                    alignItems: 'center',   // 👈 Vertically center-aligns icon with text
                    fontStretch: 'condensed', // or 'expanded', 'ultra-condensed', etc.
                    fontSize: '0.975rem',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    transition: 'opacity 0.3s ease',
                    opacity: sideNavWidth === 250 ? 1 : 0,
                    width: sideNavWidth === 250 ? 'auto' : 0,
                  }}
                >

                  {/* {brandName} */}
                  {sideNavWidth === 250 && brandName}

                </MDTypography>

                {sideNavWidth === 250 ? (
                  !isSmallScreen && (
                    <ChevronLeftIcon
                      sx={{
                        ml: 1,
                        fontSize: '20px !important',
                        color: '#fff',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        console.log('Collapse sidenav:', sideNavWidth);
                        setSideNavWidth(85);
                      }}
                    />
                  )
                ) : (
                  <ChevronRightIcon
                    sx={{
                      ml: 1,
                      fontSize: '20px !important',
                      color: '#fff',
                      cursor: 'pointer',
                      marginLeft:'-10px',
                      marginBottom:'4px',
                    }}
                    onClick={() => {
                      console.log('Expand sidenav:', sideNavWidth);
                      setSideNavWidth(250);
                    }}
                  />
                )}

              </MDBox>


            </MDBox>
          </MDBox>
        </MDBox>
        <List
          sx={{
            width: `${sideNavWidth}px`,
            transition: 'width 0.3s ease',
          }}
        >
          {renderRoutes}
        </List>

      </MDBox>



      {/* Bottom */}
      <MDBox px={2} pb={2.5}>
        <List>
          <ListItem disableGutters sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              alt={
                userObject?.first_name && userObject?.last_name
                  ? `${userObject.first_name} ${userObject.last_name}`
                  : "John Doe"
              }
              src="/images/avatar.jpg"
              sx={{ width: 40, height: 40, mr: 2,textTransform:'capitalize',backgroundColor: bgColor }}
            />

            {/* Stack Name + Info vertically */}
            <MDBox display="flex" flexDirection="column">
              <MDTypography
                variant="button"
                // fontWeight="medium"
                color="white"
                sx={{ lineHeight: 1,textTransform:'capitalize' ,fontWeight :'500',}}
              >

                {sideNavWidth === 250 ?
                  userObject?.IsAdminPlatform
                    // ? `Admin: ${userObject.first_name} ${userObject.last_name}` 
                    ? `${userObject.first_name} ${userObject.last_name}`
                    : userObject?.IsAdminCompany
                      ? `${userObject.first_name} ${userObject.last_name}`
                      // : `User: ${userObject?.first_name} ${userObject?.last_name}`
                      : `${userObject?.first_name} ${userObject?.last_name}`
                  : ''
                }


                {/* John Doe */}
              </MDTypography>

              <MDTypography
                variant="caption"
                sx={{ lineHeight: 1.3, fontSize: "0.65rem", color: '#cccccc' }}
              >
                {/* Admin | Online */}

                {
                  `${ sideNavWidth === 250 ? userObject?.email : ''}`
                }

              </MDTypography>
            </MDBox>
          </ListItem>
        </List>

      </MDBox>

    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
